import { useEffect, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";

const SESSION_KEY = "bb_intro_seen";

/**
 * Cinematic intro: the bull and bear charge in from opposite sides, clash, and
 * settle into the brand logo (video clip); the wordmark + tagline then fade in
 * beneath it. Shown once per browser session on first load, then fades out to
 * reveal the site. Skippable (click / any key), respects reduced motion (static
 * poster + instant text), and locks scroll while visible. Decorative only — the
 * page content is already in the DOM behind it.
 */

// The logo finishes assembling ~6.0s into the 8s clip; text enters just after.
const LOGO_SETTLED_S = 6.1;

// Exact logo lockup: no spaces, letters set tight, red ampersand.
const WORDMARK = Array.from("BULL&BEAR");

export function IntroSplash() {
  const reduce = useReducedMotion();
  const [show, setShow] = useState(() => {
    if (typeof window === "undefined") return false;
    try {
      return !sessionStorage.getItem(SESSION_KEY);
    } catch {
      return false;
    }
  });

  useEffect(() => {
    if (!show) return;
    try {
      sessionStorage.setItem(SESSION_KEY, "1");
    } catch {
      /* private mode — just show it this once */
    }
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const dismiss = () => setShow(false);
    // Full clip (8s) + the text sequence + a beat, then fade.
    const timer = window.setTimeout(dismiss, reduce ? 1800 : 10200);
    window.addEventListener("keydown", dismiss);
    window.addEventListener("pointerdown", dismiss);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener("keydown", dismiss);
      window.removeEventListener("pointerdown", dismiss);
      document.body.style.overflow = prevOverflow;
    };
  }, [show, reduce]);

  const textDelay = reduce ? 0 : LOGO_SETTLED_S;

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          className="fixed inset-0 z-[120] flex items-center justify-center bg-black"
          initial={{ opacity: 1 }}
          exit={{ opacity: 0, transition: { duration: 1.1, delay: reduce ? 0 : 0.4, ease: [0.16, 1, 0.3, 1] } }}
          aria-hidden="true"
        >
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={
              reduce
                ? { opacity: 0 }
                : { opacity: 0, scale: 1.08, filter: "blur(12px)", transition: { duration: 1.3, ease: [0.22, 1, 0.36, 1] } }
            }
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            className="relative flex w-full flex-col items-center px-6"
          >
            {reduce ? (
              <img
                src="/brand/intro-fight-poster.jpg"
                alt=""
                className="w-[min(94vw,900px)]"
                draggable={false}
              />
            ) : (
              // No poster: the clip opens on black, so the frame stays seamlessly
              // black while it loads instead of flashing the finished logo.
              <video
                src="/brand/intro-fight.mp4"
                autoPlay
                muted
                playsInline
                preload="auto"
                className="pointer-events-none w-[min(94vw,900px)]"
              />
            )}

            <div className="-mt-2 text-center sm:-mt-4">
              <div className="relative font-display text-[clamp(2rem,7.5vw,4.75rem)] leading-none tracking-[-0.015em] text-white">
                {reduce ? (
                  <span>
                    BULL<span className="text-accent-500">&amp;</span>BEAR
                  </span>
                ) : (
                  <>
                    {/* Letters punch in one by one, as if hammered into place */}
                    <span aria-hidden="true">
                      {WORDMARK.map((ch, i) => (
                        <motion.span
                          key={i}
                          initial={{ opacity: 0, y: 26, scale: 1.3, filter: "blur(10px)" }}
                          animate={{ opacity: 1, y: 0, scale: 1, filter: "blur(0px)" }}
                          transition={{
                            duration: 0.5,
                            delay: textDelay + i * 0.045,
                            ease: [0.16, 1, 0.3, 1],
                          }}
                          className={
                            ch === "&" ? "inline-block text-accent-500" : "inline-block"
                          }
                        >
                          {ch === " " ? "\u00A0" : ch}
                        </motion.span>
                      ))}
                    </span>
                    {/* Light sweep across the wordmark once the letters have landed */}
                    <motion.span
                      aria-hidden="true"
                      initial={{ backgroundPosition: "180% 0", opacity: 0 }}
                      animate={{ backgroundPosition: "-180% 0", opacity: [0, 1, 1, 0] }}
                      transition={{ duration: 1.1, delay: textDelay + 1.05, ease: "easeInOut" }}
                      className="pointer-events-none absolute inset-0 select-none bg-gradient-to-r from-transparent via-white to-transparent bg-clip-text text-transparent"
                      style={{ backgroundSize: "60% 100%", backgroundRepeat: "no-repeat" }}
                    >
                      BULL&amp;BEAR
                    </motion.span>
                  </>
                )}
              </div>
              <motion.div
                initial={reduce ? { opacity: 1 } : { opacity: 0, scaleX: 0 }}
                animate={{ opacity: 1, scaleX: 1 }}
                transition={{ duration: 0.7, delay: textDelay + 0.8, ease: [0.16, 1, 0.3, 1] }}
                className="mx-auto mt-4 h-px w-44 origin-center bg-gradient-to-r from-transparent via-accent-500 to-transparent"
              />
              <motion.div
                initial={reduce ? { opacity: 0 } : { opacity: 0, letterSpacing: "0.9em" }}
                animate={reduce ? { opacity: 1 } : { opacity: 1, letterSpacing: "0.45em" }}
                transition={{ duration: 0.9, delay: textDelay + 1.0, ease: [0.16, 1, 0.3, 1] }}
                className="mt-3 text-[0.7rem] uppercase text-white/55 sm:text-xs"
                style={reduce ? { letterSpacing: "0.45em" } : undefined}
              >
                Asset-Based Carrier
              </motion.div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
